import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SeoLayout from "@/components/SeoLayout";
import Jsonld, { SeoMeta } from "@/components/Jsonld";
import BookingWizard from "@/components/BookingWizard";
import { APPLIANCES, NEIGHBORHOODS, CLIMATE_CALENDAR, faqFor } from "@/data/seo";
import { apiPublic } from "@/lib/api";
import { ListChecks, MapPin, Thermometer, Sparkles, Wrench, Truck } from "lucide-react";

/**
 * Long-tail combo pages: /refrigerator-repair-stone-oak, /washer-repair-alamo-heights, etc.
 * Combo slug format: `{appliance-slug}-repair-{neighborhood-slug}`
 * 12 appliances × 8 neighborhoods = 96 pages.
 * Can be called with (app, hood) props directly by ComboDispatcher, or via URL params.
 */
export default function CityService({ app: appProp, hood: hoodProp }) {
  const params = useParams();
  let app = appProp;
  let hood = hoodProp;
  if (!app || !hood) {
    const match = (params.combo || "").match(/^(.+?)-repair-(.+)$/);
    app = APPLIANCES.find((a) => a.slug === match?.[1]);
    hood = NEIGHBORHOODS.find((n) => n.slug === match?.[2]);
  }
  const combo = `${app?.slug || "x"}-repair-${hood?.slug || "x"}`;

  const [local, setLocal] = useState(null);
  useEffect(() => {
    if (!hood) return;
    apiPublic.get(`/local/${hood.slug}`).then((r) => setLocal(r.data)).catch(() => setLocal(null));
  }, [hood]);

  if (!app || !hood) return <Navigate to="/" replace />;

  const priceLow = Math.round(app.replacement_cost * 0.1);
  const priceHigh = Math.round(app.replacement_cost * 0.35);
  const monthKey = String(new Date().getMonth() + 1).padStart(2, "0");
  const climate = CLIMATE_CALENDAR[monthKey];

  const title = `${app.display} in ${hood.name}, San Antonio · Same-Day | Space Appliance`;
  const description = `Same-day ${app.name.toLowerCase()} repair in ${hood.name} ${hood.zip}. Factory-trained on Samsung, LG, Whirlpool. Written quotes ${priceLow}–${priceHigh}. Call (210) 800-1710.`;

  // Combo-specific FAQs (override 2 from the base set with neighborhood-specific Q&A)
  const baseFaqs = faqFor(app);
  const faqs = [
    {
      q: `How fast can you reach ${hood.name} for ${app.name.toLowerCase()} repair?`,
      a: `Same-day windows for ${hood.name} (${hood.zip}) and nearby ${hood.landmarks[0]} addresses. Book before noon for an afternoon arrival; trucks roll the loop daily.`,
    },
    {
      q: `What does ${app.name.toLowerCase()} repair cost in ${hood.name}?`,
      a: `Most ${app.name.toLowerCase()} jobs in ${hood.name} land between $${priceLow}–$${priceHigh}, including a flat-rate diagnostic credited toward the repair if you proceed. We write the quote before any work begins.`,
    },
    ...baseFaqs.slice(2),
  ];

  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: app.display, name: `${app.display} in ${hood.name}, San Antonio`,
    provider: {
      "@type": "LocalBusiness", name: "Space Appliance Repair", telephone: "+1-210-800-1710", image: "/brand/logo.png",
      address: { "@type": "PostalAddress", streetAddress: "5504 Bandera Rd", addressLocality: "San Antonio", addressRegion: "TX", postalCode: "78238" },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "111" },
    },
    areaServed: { "@type": "Place", name: hood.name, geo: { "@type": "GeoCoordinates", latitude: hood.lat, longitude: hood.lng } },
    offers: { "@type": "AggregateOffer", lowPrice: priceLow, highPrice: priceHigh, priceCurrency: "USD" },
  };

  return (
    <SeoLayout>
      <SeoMeta title={title} description={description} />
      <Jsonld id={`ld-combo-svc-${combo}`} data={serviceLd} />
      <Jsonld id={`ld-combo-faq-${combo}`} data={faqLd} />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-12">
        <div className="flex flex-wrap items-center gap-2">
          <div className="chip"><Wrench className="w-3.5 h-3.5" /> {app.name}</div>
          <div className="chip"><MapPin className="w-3.5 h-3.5" /> {hood.name} · {hood.zip}</div>
        </div>
        <h1 className="display-retro mt-5 text-cream text-4xl sm:text-5xl lg:text-6xl normal-case font-semibold leading-[1.05]">
          {app.display} in <span className="text-orange">{hood.name}</span><span className="text-gold">.</span>
        </h1>
        <p className="mt-4 text-lg text-cream/80 max-w-2xl leading-relaxed">
          Same-day in-home {app.name.toLowerCase()} repair for {hood.name} ({hood.zip}). {hood.hook} Most repairs run{" "}
          <span className="text-orange font-semibold">${priceLow}–${priceHigh}</span> — written quote before any wrench turns.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#book" data-testid={`combo-book-btn`}><button className="btn-orange label-retro text-sm">Book this repair</button></a>
          <a href="tel:+12108001710"><button className="btn-ghost label-retro text-sm">(210) 800-1710</button></a>
        </div>
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <Stat icon={Truck} label={`${hood.name} trucks`} value="Daily" />
          <Stat icon={MapPin} label="Landmarks served" value={hood.landmarks.slice(0, 2).join(", ")} />
          <Stat icon={Wrench} label="First-visit fix" value="~92%" />
        </div>
      </section>

      {/* COMMON SYMPTOMS */}
      <section className="bg-navy-2/60 border-y border-cream/10 py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Most-common {app.name.toLowerCase()} symptoms in {hood.name}</div>
            <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-5">What we see</h2>
            <ul className="space-y-2.5">
              {app.common_issues.map((i) => (
                <li key={i} className="flex items-start gap-3 text-cream/85"><ListChecks className="w-4 h-4 text-orange mt-1 shrink-0" /> {i}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Live stats · last 90 days</div>
            <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-5">{hood.name} numbers</h2>
            <div className="grid grid-cols-2 gap-3">
              <Card label="Jobs nearby" value={local?.jobs_nearby ?? "—"} />
              <Card label="Avg ticket" value={local?.avg_ticket ? `$${Number(local.avg_ticket).toFixed(0)}` : "$185"} />
              <Card label="Most-failed" value={local?.top_appliance || app.name} />
              <Card label="Avg response" value={local?.avg_response_minutes ? `${local.avg_response_minutes} min` : "~22 min"} />
            </div>
          </div>
        </div>
      </section>

      {/* CLIMATE */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="bg-orange/8 border border-orange/40 rounded-xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <Thermometer className="w-6 h-6 text-orange mt-1 shrink-0" />
            <div>
              <div className="text-xs uppercase tracking-widest text-orange label-retro">{hood.name} · this month</div>
              <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mt-1">{climate.season}</h2>
              <p className="text-cream/80 mt-3">{climate.note}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {climate.top_failures.map((f) => (
                  <span key={f} className="text-xs label-retro uppercase tracking-widest border border-orange/40 text-orange rounded-full px-3 py-1.5">{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section id="book" className="py-14 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Sparkles className="w-3.5 h-3.5" /> Mission Control · {hood.name}</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Book appointment</h2>
            <p className="mt-3 text-cream/70 max-w-xl mx-auto">Same-day in {hood.name}. Code <span className="text-gold font-semibold tracking-widest">GRAND_OPENING</span> saves you $10.</p>
          </div>
          <div className="mt-10"><BookingWizard variant="embed" /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 border-t border-cream/10">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Frequently asked</div>
        <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-6">About {app.name.toLowerCase()} repair in {hood.name}</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-navy-2/60 border border-cream/15 rounded-md p-5" data-testid={`combo-faq-${combo}-${i}`}>
              <summary className="display text-cream cursor-pointer list-none flex items-center justify-between gap-4">
                <span className="text-base sm:text-lg">{f.q}</span>
                <span className="text-orange group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-cream/75 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 border-t border-cream/10 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">{app.name} repair in other neighborhoods</div>
          <div className="flex flex-wrap gap-2">
            {NEIGHBORHOODS.filter((n) => n.slug !== hood.slug).map((n) => (
              <Link key={n.slug} to={`/${app.slug}-repair-${n.slug}`} data-testid={`xlink-combo-${app.slug}-${n.slug}`}
                className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors">
                {n.name}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Other appliances in {hood.name}</div>
          <div className="flex flex-wrap gap-2">
            {APPLIANCES.filter((a) => a.slug !== app.slug).map((a) => (
              <Link key={a.slug} to={`/${a.slug}-repair-${hood.slug}`} data-testid={`xlink-combo-${a.slug}-${hood.slug}`}
                className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors">
                {a.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SeoLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-navy-2/60 border border-cream/15 rounded-md p-4 flex items-center gap-3">
      <Icon className="w-5 h-5 text-orange shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-cream/60">{label}</div>
        <div className="text-cream display-retro text-base truncate">{value}</div>
      </div>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-navy/60 border border-cream/15 rounded-md p-4 text-center">
      <div className="text-[10px] uppercase tracking-widest text-cream/60">{label}</div>
      <div className="display-retro text-orange text-xl mt-1">{value}</div>
    </div>
  );
}
